/**
 * useTrainerProfile
 *
 * Shared hook for fetching and computing trainer profile data.
 * Used by PlayPage, CampaignPage, PvpRankedMode, PvpTrialMode, etc.
 *
 * The profile + telegram status are read through React Query with app-wide shared
 * keys, so every surface (the always-mounted account menu, the Play page, the
 * Campaign pre-battle hero, PvP) reads ONE cache entry. This matters for the
 * campaign "you vs rival" hero: on a fresh page mount the profile is served from
 * the warm cache instead of starting null, so the player's own cropped avatar
 * shows immediately rather than briefly (or, on a transient fetch failure,
 * permanently) falling back to the generic Keeper portrait.
 */

import { useMemo, useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAccountId } from '../auth/useAccountId';
import type { TrainerProfile } from '@shared/api/contracts';
import type { PetWithStatus } from '@shared/domain/petModels';
import { fetchTrainerProfile, fetchTelegramLinkStatus } from '../api/petBackendApi';
import { computePrestige } from '../utils/trainerPrestige';

/** How long a fetched profile / telegram status stays fresh before a background refetch. */
const PROFILE_STALE_TIME_MS = 30_000;

/**
 * Shared React Query key for the trainer profile. The account menu and every page
 * key on this SAME tuple so the profile (and its avatar) is fetched once and shared.
 */
export const trainerProfileQueryKey = (address: string | null) =>
  ['trainerProfile', address] as const;

/** Shared key for a player's roster (`fetchOwnerPets`) — profile, referrals and the player bar. */
export const ownerPetsQueryKey = (address: string | null) =>
  ['ownerPets', address] as const;

/** Shared key for the telegram link status (matches LinkTelegramButton's key). */
export const telegramLinkQueryKey = (address: string | null) =>
  ['telegramLinkStatus', address] as const;

export interface TrainerProfileData {
  trainerProfile: TrainerProfile | null;
  telegramUsername: string | null;
  trainerLevel: number;
  topLevel: number;
  prestige: { label: string; color: string } | null;
  showProfileModal: boolean;
  setShowProfileModal: (show: boolean) => void;
  refreshProfile: () => void;
}

/**
 * Fetches trainer profile + telegram status and computes derived values
 * (trainerLevel, topLevel, prestige) from the provided pets array.
 */
export function useTrainerProfile(pets: PetWithStatus[]): TrainerProfileData {
  // Identity address (Stage B3): trainer profile + telegram status are keyed by it.
  const { address } = useAccountId();
  const queryClient = useQueryClient();
  const [showProfileModal, setShowProfileModal] = useState(false);

  const { data: trainerProfile = null } = useQuery({
    queryKey: trainerProfileQueryKey(address),
    queryFn: () => fetchTrainerProfile(address!),
    enabled: !!address,
    staleTime: PROFILE_STALE_TIME_MS,
  });

  const { data: telegramUsername = null } = useQuery({
    queryKey: telegramLinkQueryKey(address),
    queryFn: async () => {
      const status = await fetchTelegramLinkStatus(address!);
      return status.linked ? status.telegramUsername : null;
    },
    enabled: !!address,
    staleTime: PROFILE_STALE_TIME_MS,
  });

  const refreshProfile = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: trainerProfileQueryKey(address) });
    queryClient.invalidateQueries({ queryKey: telegramLinkQueryKey(address) });
  }, [queryClient, address]);

  const prestige = useMemo(() => computePrestige(pets), [pets]);

  const trainerLevel = useMemo(() => pets.reduce((sum, p) => sum + p.status.level, 0), [pets]);
  const topLevel = useMemo(() => (pets.length > 0 ? Math.max(...pets.map(p => p.status.level)) : 0), [pets]);

  return {
    trainerProfile,
    telegramUsername,
    trainerLevel,
    topLevel,
    prestige,
    showProfileModal,
    setShowProfileModal,
    refreshProfile,
  };
}
